import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, ShieldAlert, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { presentBruteForceAlert, type AuthSourceRollup } from "@/lib/brute-force-alert";
import type { Alert } from "@/types/api";

const numberFormat = new Intl.NumberFormat();

function sourceGroup(source: AuthSourceRollup): string {
  return source.organization || (source.asn ? `ASN ${source.asn}` : "Unknown network");
}

function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const maximum = Math.max(...values, 1);
  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * 88 + 2;
      const y = 22 - (value / maximum) * 18;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg viewBox="0 0 92 24" className="h-6 w-24" role="img" aria-label="Detection volume trend">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function BruteForceSummary({ alert }: { alert: Alert }) {
  const [showAll, setShowAll] = useState(false);
  const presentation = useMemo(() => presentBruteForceAlert(alert), [alert]);

  if (!presentation.isBruteForce) return null;

  const verdict =
    presentation.successStatus === "success"
      ? `SUCCESSFUL AUTH${presentation.successfulAccount ? ` · ${presentation.successfulAccount}` : ""}${
          presentation.successfulIp ? ` · ${presentation.successfulIp}` : ""
        }`
      : presentation.successStatus === "none"
        ? "NO SUCCESSFUL AUTH"
        : "SUCCESS STATUS UNKNOWN";

  const visibleSources = showAll ? presentation.sources : presentation.sources.slice(0, 10);
  const grouped = visibleSources.reduce<Map<string, AuthSourceRollup[]>>((groups, source) => {
    const key = sourceGroup(source);
    groups.set(key, [...(groups.get(key) ?? []), source]);
    return groups;
  }, new Map());

  return (
    <section aria-label="Authentication attack verdict" className="space-y-3">
      <div
        className={cn(
          "rounded-md border px-3 py-3",
          presentation.successStatus === "success" && "border-destructive bg-destructive/10 text-destructive",
          presentation.successStatus === "none" && "border-emerald-600/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300",
          presentation.successStatus === "unknown" && "border-amber-600/40 bg-amber-500/10 text-amber-900 dark:text-amber-200",
        )}
      >
        <div className="flex items-start gap-2">
          {presentation.successStatus === "success" ? (
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          ) : presentation.successStatus === "none" ? (
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          ) : (
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="font-semibold tracking-tight">{verdict}</p>
            <p className="mt-0.5 text-xs opacity-90">
              {presentation.sourceCount > 0 && `${numberFormat.format(presentation.sourceCount)} sources · `}
              {presentation.failureCount > 0 && `${numberFormat.format(presentation.failureCount)} failures · `}
              {presentation.trend !== "unknown" && `${presentation.trend} ${presentation.durationDays}d · `}
              {presentation.passwordAuthDisabled
                ? "password auth disabled on target"
                : [presentation.targetHost, presentation.targetService].filter(Boolean).join(" · ") ||
                  "target unresolved"}
            </p>
          </div>
        </div>
      </div>

      {/* A success is the incident. Keep the rest collapsed so it cannot bury the verdict. */}
      {presentation.successStatus === "success" ? null : (
        <>
          {presentation.missingFields.length > 0 && (
            <div className="rounded-md border border-amber-600/30 bg-amber-500/5 p-3 text-xs">
              <p className="font-medium">Evidence incomplete — no confident disposition</p>
              <p className="mt-1 text-muted-foreground">
                Missing: {presentation.missingFields.join(", ")}. Missing success evidence is never treated as no success.
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-xs">
            {presentation.firingCount > 1 && (
              <Badge variant="outline">RECURRING · {presentation.durationDays}d · {numberFormat.format(presentation.firingCount)} firings</Badge>
            )}
            <Sparkline values={presentation.volumeHistory} />
            {presentation.relatedDetections.length > 1 && (
              <span className="text-muted-foreground">
                {presentation.relatedDetections.length} correlated detections
              </span>
            )}
          </div>

          {(presentation.targetHost || presentation.targetService || presentation.authMethodAccepted) && (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
              <div><dt className="text-muted-foreground">Target</dt><dd className="font-medium">{[presentation.targetHost, presentation.targetService].filter(Boolean).join(" · ") || "Unknown"}</dd></div>
              <div><dt className="text-muted-foreground">Accepted auth</dt><dd className="font-medium">{presentation.authMethodAccepted || "Unknown"}</dd></div>
              <div className="col-span-2"><dt className="text-muted-foreground">Usernames targeted</dt><dd className="font-medium">{presentation.usernames.join(", ") || "Unknown"}{presentation.distinctUsernameCount != null ? ` · ${presentation.distinctUsernameCount} distinct` : ""}</dd></div>
            </dl>
          )}

          {presentation.currentWindowOnly ? (
            presentation.sources.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">Sources · current detection window</h3>
                <div className="space-y-3">
                  {[...grouped.entries()].map(([organization, sources]) => (
                    <div key={organization}>
                      <p className="mb-1 text-xs font-semibold">{organization}</p>
                      <div className="divide-y rounded-md border">
                        {sources.map((source) => (
                          <div key={source.ip} className="grid grid-cols-[1fr_auto] gap-2 px-2 py-1.5 text-xs">
                            <span className="font-mono">{source.ip}</span>
                            <span className="font-semibold tabular-nums">{numberFormat.format(source.failureCount)}</span>
                            <span className="col-span-2 text-muted-foreground">
                              {[source.asn && `AS${source.asn}`, source.country, source.firstSeenHere && `first ${source.firstSeenHere}`]
                                .filter(Boolean)
                                .join(" · ") || "Enrichment unavailable"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {presentation.sources.length > 10 && (
                  <Button variant="ghost" size="sm" className="mt-1 px-1" onClick={() => setShowAll(!showAll)}>
                    {showAll ? <ChevronDown className="mr-1 h-3.5 w-3.5" /> : <ChevronRight className="mr-1 h-3.5 w-3.5" />}
                    {showAll ? "Show top 10" : `Show all (${presentation.sources.length})`}
                  </Button>
                )}
              </div>
            )
          ) : (
            <p className="rounded-md bg-muted p-2 text-xs text-muted-foreground">
              Legacy entity history is hidden because it is not scoped to the current detection window.
            </p>
          )}
          <Link to="/app/logs" className="inline-block text-xs font-medium text-primary hover:underline">
            View raw authentication events
          </Link>
        </>
      )}
    </section>
  );
}
